/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import LoadingContent from '../../components/LoadingContent';
import SelectInput from '../../components/input/SelectInput';
import { useHistory } from 'react-router-dom';
import { appRoutes } from '../../router/router';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import toast from 'react-hot-toast';
import userProgramsService from '../../services/userProgramsService';
import wpGeodirServices from '../../services/wpGeodirServices';
import { ModalConfirm } from '../common/ModalConfirm';
import { TextField, useMediaQuery } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

interface ProgramData {
  id?: number;
  studentId: number;
  programId: number;
  programName: string;
  institution: string;
  category: string;
  applicationDeadline: string;
  daysLeft: number | null;
  status: string;
  lifecycle_status: string;
  application_status: string;
  requirementsProgress: {
    completed: number;
    total: number;
    percentage: number;
  };
  lastUpdated: string;
  program?: any;
  programCycle?: any;
  /** Raw manager row for building requirements (application_form, recommendation_letters, etc.) */
  managerRow?: any;
}

/** Requirements: application_form, recommendation_letters, transcripts, resume, essays. Completed = section has added data and status submitted/completed */
function getRequirementsProgress(row: any): { completed: number; total: number; percentage: number } {
  const total = 5;
  let completed = 0;
  const appForm = row.application_form;
  if (appForm && (appForm.status === 'submitted' || appForm.status === 'completed')) completed += 1;
  const recLetters = row.recommendation_letters;
  if (Array.isArray(recLetters) && recLetters.length > 0 && recLetters.some((r: any) => r.status === 'submitted' || r.status === 'completed')) completed += 1;
  const transcripts = row.transcripts;
  if (Array.isArray(transcripts) && transcripts.length > 0 && transcripts.some((t: any) => t.status === 'submitted' || t.status === 'completed')) completed += 1;
  const resume = row.resume;
  if (Array.isArray(resume) && resume.length > 0) completed += 1; // resume section "done" if they added at least one
  const essays = row.essays;
  if (Array.isArray(essays) && essays.length > 0 && essays.some((e: any) => e.status === 'submitted' || e.status === 'completed')) completed += 1;
  return { completed, total, percentage: total ? Math.round((completed / total) * 100) : 0 };
}

type RequirementItem = { type: string; label: string; isComplete: boolean };

function getRequirementsFromRow(row: any): RequirementItem[] {
  if (!row) return [];
  const items: RequirementItem[] = [];
  const appForm = row.application_form;
  const appComplete = appForm && (appForm.status === 'submitted' || appForm.status === 'completed');
  items.push({ type: 'application', label: 'Application Form', isComplete: !!appComplete });

  const recLetters = row.recommendation_letters;
  const recComplete = Array.isArray(recLetters) && recLetters.some((r: any) => r.status === 'submitted' || r.status === 'completed');
  items.push({ type: 'recommendation', label: 'Recommendation Letters', isComplete: !!recComplete });

  const transcripts = row.transcripts;
  const transComplete = Array.isArray(transcripts) && transcripts.some((t: any) => t.status === 'submitted' || t.status === 'completed');
  items.push({ type: 'transcript', label: 'Transcripts', isComplete: !!transComplete });

  const resume = row.resume;
  const resumeComplete = Array.isArray(resume) && resume.length > 0;
  items.push({ type: 'resume', label: 'Resume', isComplete: !!resumeComplete });

  const essays = row.essays;
  const essaysComplete = Array.isArray(essays) && essays.some((e: any) => e.status === 'submitted' || e.status === 'completed');
  items.push({ type: 'essay', label: 'Essays', isComplete: !!essaysComplete });

  return items;
}

function parseDateFlexible(s: string): Date | null {
  if (!s || typeof s !== 'string') return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  let d = new Date(trimmed);
  if (!isNaN(d.getTime())) return d;
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
        d = new Date(year, month, day);
        if (!isNaN(d.getTime())) return d;
      }
    }
  }
  return null;
}

function getDaysLeft(deadlineStr: string | undefined): number | null {
  const d = parseDateFlexible(deadlineStr || '');
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

function formatLastUpdated(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week(s) ago`;
  return d.toLocaleDateString();
}

function mapManagerToProgramData(row: any): ProgramData {
  const program = row.program || {};
  const appForm = row.application_form;
  // Prefer user's targetDate; fallback to program deadline. Treat empty string as "no value".
  const deadlineStr =
    (appForm?.targetDate?.trim() || null) ??
    program?.applicationDeadline ??
    program?.app_deadline ??
    row.applicationDeadline ??
    row.application_deadline ??
    '';
  const daysLeft = getDaysLeft(deadlineStr);
  const progress =
    row.progress != null && typeof row.progress.completed === 'number' && typeof row.progress.total === 'number'
      ? {
          completed: row.progress.completed,
          total: row.progress.total,
          percentage: row.progress.total ? Math.round((row.progress.completed / row.progress.total) * 100) : 0,
        }
      : getRequirementsProgress(row);
  const statusMap: Record<string, string> = {
    not_started: 'saved',
    in_progress: 'applying',
    submitted: 'submitted',
    completed: 'accepted',
  };
  const lifecycleStatus = statusMap[row.status] || row.status || 'saved';
  return {
    id: row.id,
    studentId: row.student_id,
    programId: row.program_id ?? program?.id,
    programName: (program.programName ?? program.program_name) || program.name || '—',
    institution: program.institution || '—',
    category: (program.primaryCategory ?? program.primary_category) || '—',
    applicationDeadline: deadlineStr || '',
    daysLeft: daysLeft ?? null,
    status: row.status || 'not_started',
    lifecycle_status: lifecycleStatus,
    application_status: lifecycleStatus,
    requirementsProgress: progress,
    lastUpdated: formatLastUpdated(row.updated_at),
    program: program,
    managerRow: row,
  };
}

const ProgramsManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    deadline: '',
    category: '',
    delivery: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<ProgramData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | string | null>(null);
  const history = useHistory();
  const isNarrowViewport = useMediaQuery('(max-width: 1315px)');

  // Status options: only not_started, in_progress, completed
  const statusOptions = [
    { value: '', name: 'All Statuses' },
    { value: 'not_started', name: 'Not Started' },
    { value: 'in_progress', name: 'In Progress' },
    { value: 'completed', name: 'Completed' },
  ];

  // Category options loaded from API (same as programs.tsx / ProgramsPage)
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; name: string }[]>([{ value: '', name: 'All Categories' }]);

  // Delivery options
  const deliveryOptions = [
    { value: '', name: 'All Types' },
    { value: 'online', name: 'Online' },
    { value: 'in-person', name: 'In-Person' },
    { value: 'hybrid', name: 'Hybrid' },
  ];

  // Format date — supports ISO, "M/D/YYYY", "MM/DD/YYYY"
  const formatDate = (dateString: string): string => {
    if (!dateString || typeof dateString !== 'string') return '';
    const s = dateString.trim();
    if (!s) return '';
    const date = parseDateFlexible(s);
    if (!date) return s;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get status badge color (Not Started, In Progress, Completed) — Complete/Completed = green
  const getStatusColor = (status: string): string => {
    const s = (status || '').trim();
    const statusMap: { [key: string]: string } = {
      'Not Started': '#6b7280',
      'In Progress': '#f59e0b',
      'Completed': '#16a34a',
      'Complete': '#16a34a',
      'Planned': '#3b82f6',
      'Submitted': '#3b82f6',
      'Accepted': '#16a34a',
      'Rejected': '#ef4444',
      'Withdrawn': '#9ca3af',
    };
    return statusMap[s] || '#6b7280';
  };

  // Get status background color
  const getStatusBgColor = (status: string): string => {
    const s = (status || '').trim();
    const statusMap: { [key: string]: string } = {
      'Not Started': '#f3f4f6',
      'In Progress': '#fef3c7',
      'Completed': '#dcfce7',
      'Complete': '#dcfce7',
      'Planned': '#dbeafe',
      'Submitted': '#dbeafe',
      'Accepted': '#dcfce7',
      'Rejected': '#fee2e2',
      'Withdrawn': '#f3f4f6',
    };
    return statusMap[s] || '#f3f4f6';
  };

  // Map lifecycle_status / status to display (API uses not_started, in_progress, completed)
  const getDisplayStatus = (lifecycleStatus: string): string => {
    const statusMap: { [key: string]: string } = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      complete: 'Complete',
      saved: 'Not Started',
      planned: 'Planned',
      applying: 'In Progress',
      submitted: 'Submitted',
      accepted: 'Accepted',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn',
    };
    return statusMap[lifecycleStatus] || lifecycleStatus;
  };

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const params: { status?: string; category?: string; deadline?: string; delivery?: string } = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.deadline) params.deadline = filters.deadline;
      if (filters.delivery) params.delivery = filters.delivery;
      const res = await userProgramsService.getProgramsManagerList(params);
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      const data = list.map(mapManagerToProgramData);
      setPrograms(data);
      setTotal(data.length);
    } catch (error: any) {
      console.error('Error loading programs manager:', error);
      toast.error(error?.response?.data?.message || 'Failed to load programs');
      setPrograms([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const list: any[] = await wpGeodirServices.getCategories();
      const arr = (list || []).map((item: any) => ({ value: item.name || String(item.id), name: item.name || '—' }));
      arr.unshift({ value: '', name: 'All Categories' });
      setCategoryOptions(arr);
    } catch {
      setCategoryOptions([{ value: '', name: 'All Categories' }]);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadPrograms();
  }, [filters.status, filters.category, filters.deadline, filters.delivery]);

  // Handle filter change
  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle pagination change
  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  // Handle row click to view details
  const handleRowClick = (record: ProgramData) => {
    history.push({
      pathname: appRoutes.summerProgramsDetail.path,
      search: `id=${record.programId}`,
    });
  };

  const handleDeleteClick = (e: React.MouseEvent, record: ProgramData) => {
    e.stopPropagation();
    setDeleteTarget(record);
  };

  const handleDeleteClose = () => {
    if (!deleting) setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await userProgramsService.deleteProgramsManager(deleteTarget.id);
      toast.success('Program removed.');
      setPrograms((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setTotal((t) => Math.max(0, t - 1));
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete program');
    } finally {
      setDeleting(false);
    }
  };

  // Handle add program
  const handleAddProgram = () => {
    history.push(appRoutes.summerProgramsBrowser.path);
  };

  // Render status badge
  const renderStatus = (record: ProgramData) => {
    const statusValue = record.lifecycle_status || record.status || 'saved';
    const displayStatus = getDisplayStatus(statusValue);
    const color = getStatusColor(displayStatus);
    const bgColor = getStatusBgColor(displayStatus);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '8px',
            backgroundColor: bgColor,
            color: color,
            fontSize: '14px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {displayStatus}
        </span>
        <ArrowDropDownIcon style={{ fontSize: '18px', color: '#6b7280', cursor: 'pointer', flexShrink: 0 }} />
      </div>
    );
  };

  // Render progress bar
  const renderProgress = (record: ProgramData) => {
    const progress = record.requirementsProgress;
    const percentage = progress ? (progress.completed / progress.total) * 100 : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px' }}>
        <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
          <div
            style={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: '#3b82f6',
              transition: 'width 0.3s',
            }}
          />
        </div>
        <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {progress ? `${progress.completed}/${progress.total}` : '0/0'}
        </span>
      </div>
    );
  };

  // Render actions
  const renderActions = (record: ProgramData) => (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <VisibilityIcon
        style={{
          fontSize: '20px',
          color: '#3b82f6',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleRowClick(record);
        }}
      />
      <FolderIcon
        style={{
          fontSize: '20px',
          color: expandedRowId === (record.id ?? record.programId) ? '#2563eb' : '#6b7280',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setExpandedRowId((prev) => (prev === (record.id ?? record.programId) ? null : (record.id ?? record.programId)));
        }}
      />
      <DeleteIcon
        style={{
          fontSize: '20px',
          color: '#ef4444',
          cursor: 'pointer',
        }}
        onClick={(e) => handleDeleteClick(e, record)}
      />
    </div>
  );

  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  return (
    <Container
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '.75rem' : '1.25rem',
        margin: '0 auto',
      }}
    >
      <div style={{ width: '100%', maxWidth: '75rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Header Section - Description + Add Program */}
        <div className="row normal" style={{ marginBottom: 0 }}>
          <div className="col-md-12">
            <div
              style={{
                backgroundColor: '#eff6ff',
                borderRadius: '12px',
                border: '1px solid #bfdbfe',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '24px',
                flexWrap: 'wrap',
              }}
            >
              <p
                style={{
                  fontSize: '16px',
                  color: '#374151',
                  margin: 0,
                  lineHeight: '1.5',
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 500,
                }}
              >
                Track the programs you're applying to — deadlines, requirements, and progress all in one place.
              </p>
              <button
                onClick={handleAddProgram}
                style={{
                  backgroundColor: '#2B6BFB',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexShrink: 0,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#235ee0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2B6BFB';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Add Program
              </button>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="row normal" style={{ marginBottom: 0 }}>
          <div className="col-md-12">
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
              }}
            >
              <div className="d-flex flex-wrap gap-3" style={{ alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', minWidth: '180px', minHeight: '50px' }}>
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>Status:</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectInput
                      label=""
                      name="status"
                      option={statusOptions}
                      values={filters.status ?? ''}
                      onChange={(e: any) => {
                        const value = e.target?.value || e.value || '';
                        handleFilterChange('status', value);
                      }}
                      placeholder="All Statuses"
                      disabled={loading}
                      renderValue={(selected: any) => {
                        const v = selected?.value ?? selected ?? '';
                        const o = statusOptions.find((opt) => opt.value === v);
                        return o ? o.name : 'All Statuses';
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flex: isNarrowViewport ? '1 0 280px' : '1 1 220px',
                    minWidth: isNarrowViewport ? '280px' : '240px',
                    flexShrink: isNarrowViewport ? 0 : 1,
                    minHeight: '50px',
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>Deadline:</span>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <DatePicker
                      value={filters.deadline ? moment(filters.deadline) : null}
                      onChange={(val) => handleFilterChange('deadline', val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '')}
                      disabled={loading}
                      inputFormat="MM/DD/yyyy"
                      maxDate={moment('9999-12-31')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder="MM/DD/yyyy"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              borderRadius: '0.5rem',
                              height: '50px',
                              minHeight: '50px',
                              maxHeight: '50px',
                              paddingLeft: '14px',
                              paddingRight: '14px',
                              alignItems: 'center',
                              boxSizing: 'border-box',
                              '& fieldset': { borderRadius: '0.5rem', borderColor: '#e5e7eb' },
                              '& .MuiInputAdornment-root': {
                                position: 'relative',
                                marginLeft: 0,
                                marginRight: 0,
                              },
                              '& .MuiIconButton-root': {
                                padding: '8px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              paddingTop: 0,
                              paddingBottom: 0,
                              lineHeight: '1.5',
                              fontSize: '14px',
                              fontWeight: 400,
                              height: '100%',
                              boxSizing: 'border-box',
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', minWidth: '180px', minHeight: '50px' }}>
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>Category:</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectInput
                      label=""
                      name="category"
                      option={categoryOptions}
                      values={filters.category ?? ''}
                      onChange={(e: any) => {
                        const value = e.target?.value || e.value || '';
                        handleFilterChange('category', value);
                      }}
                      placeholder="All Categories"
                      disabled={loading}
                      renderValue={(selected: any) => {
                        const v = selected?.value ?? selected ?? '';
                        const o = categoryOptions.find((opt) => opt.value === v);
                        return o ? o.name : 'All Categories';
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', minWidth: '180px', minHeight: '50px' }}>
                  <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>Delivery:</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectInput
                      label=""
                      name="delivery"
                      option={deliveryOptions}
                      values={filters.delivery ?? ''}
                      onChange={(e: any) => {
                        const value = e.target?.value || e.value || '';
                        handleFilterChange('delivery', value);
                      }}
                      placeholder="All Types"
                      disabled={loading}
                      renderValue={(selected: any) => {
                        const v = selected?.value ?? selected ?? '';
                        const o = deliveryOptions.find((opt) => opt.value === v);
                        return o ? o.name : 'All Types';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Program List Table */}
        {loading ? (
          <LoadingContent />
        ) : (
          <div className="row normal">
            <div className="col-md-12">
              <div
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1.5fr 1.2fr 1.5fr 1fr 120px',
                    gap: '16px',
                    padding: '16px 24px',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontWeight: 600,
                    fontSize: '12px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  <div>PROGRAM NAME</div>
                  <div>INSTITUTION</div>
                  <div>APPLICATION DEADLINE</div>
                  <div>STATUS</div>
                  <div>REQUIREMENTS PROGRESS</div>
                  <div>LAST UPDATED</div>
                  <div style={{ textAlign: 'right' }}>ACTIONS</div>
                </div>

                {/* Table Body */}
                {programs.length > 0 ? (
                  programs.map((record, index) => {
                    const rowKey = record.id ?? record.programId ?? index;
                    const isExpanded = expandedRowId === rowKey;
                    const requirements = getRequirementsFromRow(record.managerRow);
                    return (
                    <div key={rowKey} style={{ borderBottom: index < programs.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <div
                      onClick={() => handleRowClick(record)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1.5fr 1.5fr 1.2fr 1.5fr 1fr 120px',
                        gap: '16px',
                        padding: '20px 24px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {/* PROGRAM NAME - clickable to navigate */}
                      <div>
                        <div
                          style={{
                            fontWeight: 500,
                            color: '#2563eb',
                            marginBottom: '4px',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(record);
                          }}
                        >
                          {record.programName}
                        </div>
                        {record.category && (
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {record.category}
                          </div>
                        )}
                      </div>

                      {/* INSTITUTION */}
                      <div style={{ color: '#374151', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                        {record.institution}
                      </div>

                      {/* APPLICATION DEADLINE */}
                      <div>
                        <div style={{ color: '#111827', fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>
                          {record.applicationDeadline ? formatDate(record.applicationDeadline) : '—'}
                        </div>
                        {record.daysLeft != null && (
                          <div
                            style={{
                              fontSize: '13px',
                              color: record.daysLeft < 0 ? '#ef4444' : record.daysLeft <= 7 ? '#f59e0b' : '#10b981',
                            }}
                          >
                            {record.daysLeft < 0
                              ? `${Math.abs(record.daysLeft)} days ago`
                              : `${record.daysLeft} days left`}
                          </div>
                        )}
                      </div>

                      {/* STATUS */}
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {renderStatus(record)}
                      </div>

                      {/* REQUIREMENTS PROGRESS */}
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {renderProgress(record)}
                      </div>

                      {/* LAST UPDATED */}
                      <div style={{ color: '#6b7280', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                        {record.lastUpdated}
                      </div>

                      {/* ACTIONS */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {renderActions(record)}
                      </div>
                    </div>
                    {isExpanded && (
                      <div
                        style={{
                          padding: '16px 24px 20px',
                          backgroundColor: '#fafafa',
                          borderTop: '1px solid #e5e7eb',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#9ca3af',
                            letterSpacing: '0.05em',
                            marginBottom: 12,
                          }}
                        >
                          REQUIREMENTS
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {requirements.map((req) => (
                            <div
                              key={req.type}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '12px 16px',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              {req.isComplete ? (
                                <CheckCircleIcon sx={{ fontSize: 22, color: '#16a34a', flexShrink: 0 }} />
                              ) : (
                                <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: '#d1d5db', flexShrink: 0 }} />
                              )}
                              <span style={{ flex: 1, fontSize: '14px', color: '#374151' }}>
                                {req.label}
                              </span>
                              <span
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '9999px',
                                  backgroundColor: req.isComplete ? '#dcfce7' : '#f3f4f6',
                                  color: req.isComplete ? '#16a34a' : '#6b7280',
                                }}
                              >
                                {req.isComplete ? 'Complete' : 'Incomplete'}
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(record);
                          }}
                          style={{
                            marginTop: 12,
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: 0,
                            textDecoration: 'underline',
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    )}
                    </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                    No programs found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalConfirm
        open={!!deleteTarget}
        handleClose={handleDeleteClose}
        handleConfirm={handleDeleteConfirm}
        title="Remove program?"
        message={
          deleteTarget
            ? `Are you sure you want to remove "${deleteTarget.programName}" from your list? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        showWarningIcon
      />
    </Container>
  );
};

export default ProgramsManager;
